import { AlertsTypes } from '~/const/notification';
import { getUniqueIdSimple } from '~/helpers';
import type { INotificationGetByRequest, TAlert, TNotification } from '~~/shared/interfaces/notifications';
import { useBaseStore } from '~/store/base.store';

export const useNotificationStore = defineStore('notification', () => {
	const baseStore = useBaseStore();
	const notificationsRequest = ref<INotificationGetByRequest>(null);
	const countGetErrorNotification = ref(0);
	const alerts = ref<TAlert[]>([]);
	const timeouts = ref<Record<string | number, NodeJS.Timeout>>({});
	const loading = ref<Record<string, any>>({
		getNotifications: false,
		deleteAllNotificationBtn: false,
		deleteNotification: {}
	});

	function $reset() {
		alerts.value = [];
		notificationsRequest.value = null;
	}

	const deleteNotification = async (id: string) => {
		const { data, status } = await useCustomFetch<boolean>(
			'/api/Notifications/mark-as-readed',
			{
				server: false,
				method: 'put',
				body: [id]
			},

			{
				setLoading: (load) => {
					loading.value.deleteNotification = { id: load };
				}
			}
		);

		if (data.value === true) {
			notifications.value = notifications.value.filter((notification) => notification.id !== id);
		}
	};

	const deleteAllNotification = async () => {
		const { data, status } = await useCustomFetch<boolean>(
			'/api/Notifications/mark-all-as-readed',
			{
				server: false,
				method: 'put'
			},
			{
				setLoading: (load) => {
					loading.value.deleteAllNotificationBtn = load;
				}
			}
		);

		if (data.value === true) {
			notificationsRequest.value = null;
		}
	};

	const getNotifications = async (payload: { pageSize: number } = { pageSize: 1000 }) => {
		const { data, status } = await useCustomFetch<INotificationGetByRequest>(
			'/api/UserNotification/get-by-request',
			{
				method: 'post',
				body: payload
			},
			{
				setLoading: (load) => {
					loading.value.getNotifications = load;
				}
			}
		);

		if (status.value === 'success' && data.value) {
			notificationsRequest.value = data.value;
		}
	};

	const readAllNotifications = async () => {
		const { data, status } = await useCustomFetch<boolean>('/api/UserNotification/mark-all-as-read', {
			method: 'post'
		});

		if (status.value === 'success' && data.value) {
			getNotifications();
		}
	};

	// alerts

	const addNewAlert = (message: string, timeDead: number = 1000, type: AlertsTypes = AlertsTypes.FAILURE) => {
		const id = getUniqueIdSimple();
		alerts.value.push({ id: id, message, type });
		deleteAlert(id, timeDead);
	};

	const deleteAlert = (id: number, timeDead: number, isNeedClearAll?: boolean) => {
		timeouts.value[id] = setTimeout(() => {
			if (isNeedClearAll) {
				alerts.value = [];
				return;
			}
			alerts.value = alerts.value.filter((alert) => alert.id !== id);
		}, timeDead);
	};

	const cancelAutomaticAlertRemoval = (id: number) => {
		clearTimeout(timeouts.value[id]);
	};

	return {
		alerts,
		notificationsRequest,
		loading,
		countGetErrorNotification,
		$reset,
		addNewAlert,
		deleteAlert,
		deleteNotification,
		deleteAllNotification,
		getNotifications,
		cancelAutomaticAlertRemoval,
		readAllNotifications
	};
});
